import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LayoutDashboard, LogOut, User, UserX } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// Import de nos composants UI personnalisés
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';

interface UserMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export const UserMenu = ({ anchorEl, onClose }: UserMenuProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentUser, originalUser, isImpersonating, logout, stopImpersonation } = useAuth();

  console.log('UserMenu render:', { currentUser, originalUser, isImpersonating });
  console.log('🟦 stopImpersonation function available:', typeof stopImpersonation, stopImpersonation);

  const [isOpen, setIsOpen] = useState(Boolean(anchorEl));

  // Mettre à jour l'état d'ouverture quand anchorEl change
  useEffect(() => {
    setIsOpen(Boolean(anchorEl));
  }, [anchorEl]);

  // Force re-render when impersonation state changes
  useEffect(() => {
    console.log('🟣 isImpersonating changed:', isImpersonating);
  }, [isImpersonating, originalUser, currentUser]);

  const getFirstName = (fullName: string | undefined) => {
    if (!fullName) return '';
    const trimmedName = fullName.trim();
    if (!trimmedName) return '';
    return trimmedName.split(' ')[0];
  };

  const displayName = currentUser && currentUser.name ? getFirstName(currentUser.name) : '';

  const handleStopImpersonation = () => {
    console.log('🟡 === handleStopImpersonation START ===');
    console.log('🟡 Calling stopImpersonation...');
    stopImpersonation();
    console.log('🟡 === handleStopImpersonation END ===');
    
    // Forcer la fermeture après un délai
    setTimeout(() => {
      setIsOpen(false);
      handleClose();
    }, 100);
  };

  const handleDashboardClick = () => {
    setIsOpen(false);
    navigate('/app/adblock-bypass');
  };

  const handleClose = () => {
    setIsOpen(false);
    onClose();
  };

  if (!currentUser) return null;
  
  // Mise à jour du gestionnaire de clic pour le bouton de déconnexion
  const logoutAndRedirect = async (e: React.MouseEvent) => {
    e.stopPropagation();
    console.log("Tentative de déconnexion via div de déconnexion");
    
    try {
      // Fermer le menu d'abord
      handleClose();
      
      // Appeler la fonction de déconnexion de Firebase
      await logout();
      console.log("Déconnexion Firebase réussie, redirection...");
      
      // Redirection après déconnexion réussie
      window.location.replace('/login');
    } catch (error) {
      console.error("Erreur lors de la déconnexion:", error);
      window.location.replace('/login');
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={(open) => {
      console.log('Popover onOpenChange:', open);
      // Ne JAMAIS fermer automatiquement si on est en mode impersonation
      if (!open && !isImpersonating) {
        handleClose();
      }
    }}>
      <PopoverTrigger asChild>
        <button type="button" className="w-0 h-0 overflow-hidden">
          Trigger
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0 bg-white dark:bg-slate-900 border dark:border-slate-800 rounded-xl shadow-lg">
        <div className="p-6 space-y-6">
          {/* User Info Section */}
          <div className="mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-50 dark:bg-slate-800 rounded-full">
                <User size={24} className="text-gray-700 dark:text-slate-200" />
              </div>
              <div>
                <h3 className="text-gray-900 dark:text-slate-100 font-medium text-base">
                  {displayName || t('user.anonymous')}
                </h3>
                <p className="text-gray-600 dark:text-slate-300 text-sm">
                  {currentUser?.role === 'ADMIN' ? t('user.role.admin') : t('user.role.client')}
                  {currentUser?.role !== 'ADMIN' && currentUser?.networks && ` - ${currentUser.networks.join(', ')}`}
                </p>
                {isImpersonating && originalUser && (
                  <p className="text-orange-600 dark:text-orange-400 text-xs mt-1">
                    {t('user.impersonating')} • {t('user.originalUser')}: {getFirstName(originalUser.name || '')}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Actions Section */}
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start py-2 px-4 rounded-xl text-gray-700 dark:text-slate-200 hover:bg-gray-50 dark:hover:bg-slate-800"
              onClick={handleDashboardClick}
            >
              <LayoutDashboard size={20} className="mr-2" />
              {t('user.dashboard')}
            </Button>

            {/* Bouton pour arrêter l'impersonation - visible seulement en mode impersonation */}
            {isImpersonating && (
              <>
                <Separator className="dark:border-slate-800" />
                <Button
                  variant="ghost"
                  className="w-full justify-start py-2 px-4 rounded-xl text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                  onMouseDown={(e) => {
                    console.log('🟢 Exit Impersonate BUTTON MOUSE DOWN!');
                    e.preventDefault();
                    e.stopPropagation();
                    handleStopImpersonation();
                  }}
                >
                  <UserX size={20} className="mr-2" />
                  {t('user.stopImpersonation')}
                </Button>
                {console.log('Exit Impersonate button rendered')}
              </>
            )}

            <Separator className="dark:border-slate-800" />
            
            {/* Bouton de déconnexion */}
            <div 
              className="w-full flex justify-start py-2 px-4 mt-2 rounded-xl text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 cursor-pointer"
              onClick={logoutAndRedirect}
              onMouseDown={(e) => {
                e.stopPropagation();
                console.log("MouseDown sur div de déconnexion");
              }}
              data-testid="logout-div"
            >
              <LogOut size={20} className="mr-2" />
              <span>{t('user.logout')}</span>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};